import React from "react";
import {Link} from "react-router-dom";
import ReactTable from "react-table";
import {Button, ButtonGroup} from "reactstrap";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Edit from "@material-ui/icons/Edit";
import moment from "moment";

import API from "@beardeddevops/react.api";
import Framework from "@beardeddevops/react.framework";
import Types from "constants/Types";
import {Objects} from "components";
import MembershipBillingAlert from "components/Elements/MembershipBillingAlert";
import {membershipIncludesCustomer, membershipRole} from "utils/membershipCoveredMembers";
import {
	membershipHasFailedBilling as hasFailedBilling,
	membershipMonthlyValue,
	resolvedAccountMembershipMonthlyValue,
} from "utils/membershipSummary";

const toastOptions = {
	position: API.Toast.POSITION && API.Toast.POSITION.TOP_CENTER ? API.Toast.POSITION.TOP_CENTER : "top-center",
};

const settingEnabled = (value) => value === true || value === 1 || value === "1" || value === "true";

const formatCurrency = (value) => {
	const amount = parseFloat(value);
	return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "$0.00";
};

const normalizeBillingPeriod = (membership) => (
	membership && membership.billing_period === "yearly" ? "yearly" : "monthly"
);

const billingPeriodUnitLabel = (billingPeriod) => (
	billingPeriod === "yearly" ? "year" : "month"
);

const formatBillingAmount = (membership) => (
	`${formatCurrency(membership.cost_per_month)} / ${billingPeriodUnitLabel(normalizeBillingPeriod(membership))}`
);

const isFreeMembership = (membership) => parseFloat(membership && membership.cost_per_month) === 0;

const membershipEndDate = (membership) => {
	const endDate = membership && membership.end_date
		? moment(membership.end_date, "YYYY-MM-DD", true)
		: null;
	return endDate && endDate.isValid() ? endDate : null;
};

const hasMembershipEnded = (membership) => {
	const endDate = membershipEndDate(membership);
	return !!endDate && endDate.isBefore(moment(), "day");
};

const hasMembershipReachedBillingEnd = (membership) => {
	const endDate = membershipEndDate(membership);
	return !!endDate && !endDate.isAfter(moment(), "day");
};

const hasNextBillAfterMembershipEnd = (membership) => {
	const endDate = membershipEndDate(membership);
	const nextBillDate = membership && membership.next_bill_date
		? moment(membership.next_bill_date, "YYYY-MM-DD", true)
		: null;
	return !!endDate && !!nextBillDate && nextBillDate.isValid() && nextBillDate.isAfter(endDate, "day");
};

const formatRangeTime = (membership) => {
	const totalMinutes = parseInt(membership.free_range_minutes, 10) || 0;
	if (typeof membership.range_minutes_balance === "undefined" || membership.range_minutes_balance === null) {
		return `${totalMinutes} min`;
	}

	const balanceMinutes = parseInt(membership.range_minutes_balance, 10) || 0;
	return `${balanceMinutes} / ${totalMinutes} min`;
};

const formatDate = (value) => value ? moment(value).format("MM/DD/YYYY") : "Not set";

const failedBillingCount = (membership) => parseInt(membership.failed_billing_count, 10) || 0;

const normalizedMembershipStatus = (membership) => String(membership.status || "").trim().toLowerCase();
const isCoveredMembership = (membership) => membershipRole(membership) === "covered";
const isRemovedCoveredMembership = (membership) => membershipRole(membership) === "removed";

const payingCustomerName = (membership) => (
	(membership.paying_customer && membership.paying_customer.name)
	|| (membership.customers && membership.customers.name)
	|| "Paying member"
);

const customerMembershipBillingAmount = (membership) => (
	isCoveredMembership(membership) || isRemovedCoveredMembership(membership)
		? "No individual charge"
		: formatBillingAmount(membership)
);

const customerMembershipCardLabel = (membership) => (
	isCoveredMembership(membership) || isRemovedCoveredMembership(membership)
		? "Payer card"
		: formatCardLabel(membership)
);

const isDeletedMembership = (membership) => normalizedMembershipStatus(membership) === "deleted";

const isCanceledMembership = (membership) => (
	!!membership.cancelled_at || ["canceled", "cancelled"].indexOf(normalizedMembershipStatus(membership)) !== -1
);

const isActiveMembership = (membership) => {
	const status = normalizedMembershipStatus(membership);
	return !isRemovedCoveredMembership(membership)
		&& !membership.cancelled_at
		&& !hasMembershipEnded(membership)
		&& ["paused", "canceled", "cancelled", "deleted"].indexOf(status) === -1;
};

const isBillingAttentionMembership = (membership) => (
	isCanceledMembership(membership)
	|| (membership.last_billing_status || "").toLowerCase() === "failed"
	|| failedBillingCount(membership) > 0
);

const isCurrentMembership = (membership) => isActiveMembership(membership) && !hasFailedBilling(membership);

const customerPhone = (membership) => (
	(membership.customers && membership.customers.phone) || membership.customer_phone || ""
);

const customerPhoneHref = (membership) => {
	const phone = customerPhone(membership).replace(/[^\d+]/g, "");
	return phone ? `tel:${phone}` : "";
};

const billingAttentionStatus = (membership) => isCanceledMembership(membership) ? "Cancelled" : "Failed";

const membershipActivityStatus = (membership) => {
	if (isRemovedCoveredMembership(membership)) {
		return "Removed";
	}
	if (membership.display_status) {
		return membership.display_status;
	}
	if (isDeletedMembership(membership)) {
		return "Deleted";
	}
	if (isCanceledMembership(membership)) {
		return "Cancelled";
	}
	if (normalizedMembershipStatus(membership) === "paused") {
		return "Paused";
	}
	if (hasFailedBilling(membership)) {
		return "Failed";
	}

	return "Current";
};

const membershipActivityStatusClass = (membership) => {
	const status = membershipActivityStatus(membership);
	if (["Cancelled", "Deleted", "Failed"].indexOf(status) !== -1) {
		return "failed";
	}
	if (status === "Paused") {
		return "pending";
	}
	if (["Expired", "Removed"].indexOf(status) !== -1) {
		return "neutral";
	}

	return "success";
};

const membershipActivityUrl = (membership) => {
	const filters = [
		`membership=${parseInt(membership.pkey, 10)}`,
		"expand=1",
	];

	return `/memberships/history?${filters.join("&")}`;
};

const customerMembershipActivityUrl = (customer) => `/memberships/history?customer=${parseInt(customer, 10)}&expand=1`;

const failedBillingDetailsUrl = (membership = null, customer = null) => {
	const filters = [
		"event_type=membership_billing_failed",
		"status=failed",
		"expand=1",
	];
	const membershipPkey = membership ? parseInt(membership.pkey, 10) : 0;

	if (Number.isFinite(membershipPkey) && membershipPkey > 0) {
		filters.push(`membership=${membershipPkey}`);
	}
	const customerPkey = parseInt(customer || (membership && membership.customer), 10);
	if (Number.isFinite(customerPkey) && customerPkey > 0) {
		filters.push(`customer=${customerPkey}`);
	}

	return `/memberships/history?${filters.join("&")}`;
};

const parseInventoryGroupDiscounts = (value) => {
	if (Array.isArray(value)) {
		return value;
	}
	if (!value) {
		return [];
	}
	try {
		const parsed = JSON.parse(value);
		return Array.isArray(parsed) ? parsed : [];
	} catch (error) {
		return [];
	}
};

const formatInventoryGroupDiscounts = (membership) => {
	const discounts = parseInventoryGroupDiscounts(membership.inventory_group_discounts);
	if (!discounts.length) {
		const fallbackDiscount = parseFloat(membership.discount_percentage || 0);
		return fallbackDiscount ? `${fallbackDiscount.toFixed(2)}%` : "No group discounts";
	}

	return discounts.map((discount) => {
		const groupName = discount.inventory_group_name
			|| (discount.sku_classes && discount.sku_classes.name)
			|| (discount.applies_to_all ? "All Items" : null)
			|| "Inventory Group";
		const discountAmount = parseFloat(discount.discount_amount || 0);
		if (Number.isFinite(discountAmount) && discountAmount > 0) {
			return `${groupName}: ${formatCurrency(discountAmount)}`;
		}
		return `${groupName}: ${parseFloat(discount.discount_percentage || 0).toFixed(2)}%`;
	}).join(", ");
};

const formatCustomerName = (membership) => {
	if (membership.customers && membership.customers.name) {
		return membership.customers.name;
	}

	return "No customer";
};

const formatTerminalName = (membership) => {
	const terminal = membership.altrupay_spin_processing;
	if (!terminal) {
		return "No terminal";
	}

	return terminal.nickname || terminal.tpn || "Terminal";
};

const hasCardToken = (membership) => !!(membership.card_last_four || membership.tokenized_at);

const formatCardLabel = (membership) => hasCardToken(membership)
	? `${membership.card_brand || "Card"} ${membership.card_last_four ? `**** ${membership.card_last_four}` : ""}`
	: "No card";

const getMembershipPlan = (membership) => {
	const plan = parseInt(membership.membership_plan || membership.MembershipPlan || 0, 10);
	return Number.isFinite(plan) ? plan : 0;
};

const isCustomerMembership = (membership) => !!(
	membership.customer
	|| membership.customers
	|| hasCardToken(membership)
	|| getMembershipPlan(membership)
);

const isMembershipPlan = (membership) => !isCustomerMembership(membership);

const membershipMatchesPlan = (membership, plan) => {
	const membershipPlan = getMembershipPlan(membership);
	if (membershipPlan) {
		return membershipPlan === parseInt(plan.pkey, 10);
	}

	return isCustomerMembership(membership) && membership.name === plan.name;
};

const membershipMatchesCustomer = (membership, customer) => membershipIncludesCustomer(membership, customer);

const canManageMembershipPlansForRole = (roles) => {
	const role = parseInt(roles, 10);
	return role !== 2;
};

const MEMBERSHIP_SEARCH_DEBOUNCE_MS = 1000;

export default class Memberships extends Framework.Components.Collection {
	constructor(props) {
		let model = new API.BuildClass(Types.Memberships._name, API.RequestType.READ_ALL);
		super(props, model);
		this.state.limit = 100;
		this.limit = 100;
		this.state.activeView = props.customer || !canManageMembershipPlansForRole(props.roles) ? "customers" : "plans";
		this.state.selectedPlan = null;
		this.state.selectedPlanFilter = null;
		this.state.enrollmentModal = false;
		this.state.membershipPlans = [];
		this.state.membershipPlansLoading = true;
		this.state.accountMonthlyValue = null;
		this.state.allowMultipleMemberships = false;
		this.state.coveredMembersReady = false;
		this.state.coveredMemberLimit = 25;
		this.state.membershipSettingsLoading = true;
		this.state.membershipSettingsSaving = false;
		this.state.retryingMembership = null;
		this.state.billingStatusFilter = "all";
		this.state.filter = {
			membership_kind: props.customer ? "customer_activity" : "enrollment",
			...(props.customer ? {customer: props.customer} : {}),
		};
		if (props.customer) {
			this.tableStorageName = `${this.constructor.name}-customer-${props.customer}`;
		}
		this.tableState = null;
		this.enrollmentFetchInFlight = false;
		this.enrollmentFetchQueued = false;
		this.queuedEnrollmentTableState = null;
		this.retryRequestInFlight = false;
		this.membershipSearchTimeout = null;
		// Enable Collection search wiring immediately; memberships loads outside the default fetchData path.
		this.loaded = 3;
	}

	componentDidMount() {
		this.fetchMembershipSettings();
		this.fetchPlans();
		if (this.effectiveActiveView() === "customers") {
			this.fetchEnrollments();
		}
	}

	componentWillUnmount() {
		if (this.membershipSearchTimeout) {
			clearTimeout(this.membershipSearchTimeout);
			this.membershipSearchTimeout = null;
		}
		if (this.timeout) {
			clearTimeout(this.timeout);
			this.timeout = null;
		}
	}

	componentDidUpdate(prevProps, prevState) {
		// Debounce search until typing stops so large tables are not refetched on every keystroke.
		if (prevState.search !== this.state.search) {
			if (this.timeout) {
				clearTimeout(this.timeout);
				this.timeout = null;
			}
			if (this.membershipSearchTimeout) {
				clearTimeout(this.membershipSearchTimeout);
			}
			this.membershipSearchTimeout = setTimeout(() => {
				this.membershipSearchTimeout = null;
				this.reloadMembershipSearch();
			}, MEMBERSHIP_SEARCH_DEBOUNCE_MS);
		}

		if (prevProps.customer !== this.props.customer) {
			this.fetchPlans();
			this.setState({
				filter: this.buildEnrollmentFilter({
					customer: this.props.customer,
				}),
				page: 0,
				pages: null,
			}, () => {
				this.fetchEnrollments();
			});
		}
		if (prevProps.roles !== this.props.roles && !this.canManageMembershipPlans() && this.state.activeView !== "customers") {
			this.setState({
				activeView: "customers",
				selectedPlanFilter: null,
				filter: this.buildEnrollmentFilter({selectedPlanFilter: null}),
				page: 0,
				pages: null,
			}, () => {
				this.fetchEnrollments();
			});
		}
		if (
			prevState.activeView !== this.state.activeView
			&& this.state.activeView !== "plans"
			&& !this.props.customer
		) {
			this.fetchEnrollments();
		}
	}

	canManageMembershipPlans = () => canManageMembershipPlansForRole(this.props.roles);

	effectiveActiveView = () => (
		this.props.customer || !this.canManageMembershipPlans() ? "customers" : this.state.activeView
	);

	fetchMembershipSettings = async () => {
		this.setState({membershipSettingsLoading: true});
		try {
			const requestType = "GET_SETTINGS";
			const requestModel = new API.BuildClass(Types.Memberships._name, requestType);
			const settings = await requestModel.submit(null, requestType, true);
			this.setState({
				allowMultipleMemberships: settingEnabled(settings && settings.allow_multiple_memberships),
				coveredMembersReady: settingEnabled(settings && settings.covered_members_ready),
				coveredMemberLimit: parseInt(settings && settings.covered_member_limit, 10) || 25,
				membershipSettingsLoading: false,
			});
		} catch (error) {
			API.Toast.error(error.message || "Unable to load membership settings.", toastOptions);
			this.setState({membershipSettingsLoading: false});
		}
	};

	updateAllowMultipleMemberships = async (allowMultipleMemberships) => {
		if (this.state.membershipSettingsSaving || this.state.allowMultipleMemberships === allowMultipleMemberships) {
			return;
		}

		const previousAllowMultipleMemberships = this.state.allowMultipleMemberships;
		this.setState({
			allowMultipleMemberships,
			membershipSettingsSaving: true,
		});

		try {
			const requestType = "POST_SETTINGS";
			const requestModel = new API.BuildClass(Types.Memberships._name, requestType);
			requestModel.object = {allow_multiple_memberships: allowMultipleMemberships};
			const settings = await requestModel.submit(null, requestType, true);
			this.setState({
				allowMultipleMemberships: settingEnabled(settings && settings.allow_multiple_memberships),
				membershipSettingsSaving: false,
			});
			API.Toast.success("Membership settings saved.", toastOptions);
		} catch (error) {
			this.setState({
				allowMultipleMemberships: previousAllowMultipleMemberships,
				membershipSettingsSaving: false,
			});
			API.Toast.error(error.message || "Unable to save membership settings.", toastOptions);
		}
	};

	fetchPlans = async () => {
		const hasPlans = (this.state.membershipPlans || []).length > 0;
		// Avoid blanking the table on search refreshes; only show loading on the first load.
		if (!hasPlans) {
			this.setState({membershipPlansLoading: true});
		}
		try {
			const planModel = new API.BuildClass(Types.Memberships._name, API.RequestType.READ_ALL);
			planModel.limit = 0;
			planModel.offset = 0;
			planModel.sort = "name";
			planModel.order = "asc";
			planModel.search = this.state.search || "";
			if (typeof this.state.searchField !== "undefined" && this.state.searchField !== "") {
				planModel.search_field = this.state.searchField.value || this.state.searchField;
			}
			planModel.filter = {membership_kind: "plan"};
			const response = await planModel.submit(null, API.RequestType.READ_ALL);
			const membershipPlans = ((response && response.items) || []).filter(isMembershipPlan);
			const accountMonthlyValue = parseFloat(response && response.monthly_value);
			this.setState({
				membershipPlans,
				membershipPlansLoading: false,
				accountMonthlyValue: Number.isFinite(accountMonthlyValue) ? accountMonthlyValue : null,
			});
		} catch (error) {
			this.setState({membershipPlansLoading: false});
		} finally {
			// Plans load outside Collection.fetchData; advance bootstrap state so enrollment fetches are not blocked.
			this.loaded = 3;
		}
	};

	buildEnrollmentFilter = (overrides = {}) => {
		const activeView = Object.prototype.hasOwnProperty.call(overrides, "activeView")
			? overrides.activeView
			: this.effectiveActiveView();
		const selectedPlanFilter = Object.prototype.hasOwnProperty.call(overrides, "selectedPlanFilter")
			? overrides.selectedPlanFilter
			: this.state.selectedPlanFilter;
		const customer = Object.prototype.hasOwnProperty.call(overrides, "customer")
			? overrides.customer
			: this.props.customer;
		const billingStatusFilter = Object.prototype.hasOwnProperty.call(overrides, "billingStatusFilter")
			? overrides.billingStatusFilter
			: this.state.billingStatusFilter;
		const billingMembershipKind = billingStatusFilter === "failed"
			? "billing_attention_failed"
			: (billingStatusFilter === "cancelled" ? "billing_attention_cancelled" : "billing_attention");
		const filter = {
			membership_kind: activeView === "billing" ? billingMembershipKind : (customer ? "customer_activity" : "enrollment"),
		};

		if (customer) {
			filter.customer = customer;
		}
		if (selectedPlanFilter && selectedPlanFilter.pkey) {
			filter.membership_plan = selectedPlanFilter.pkey;
		}

		return filter;
	};

	getEnrollments = () => {
		const data = Array.isArray(this.state.data) ? this.state.data : [];
		return data
			.filter(isCustomerMembership)
			.filter(isActiveMembership)
			.filter((membership) => !this.props.customer || membershipMatchesCustomer(membership, this.props.customer));
	};

	getCustomerActivityMemberships = () => {
		const data = Array.isArray(this.state.data) ? this.state.data : [];
		return data
			.filter(isCustomerMembership)
			.filter((membership) => !this.props.customer || membershipMatchesCustomer(membership, this.props.customer));
	};

	getBillingAttentionMemberships = () => {
		const data = Array.isArray(this.state.data) ? this.state.data : [];
		return data
			.filter(isCustomerMembership)
			.filter(isBillingAttentionMembership);
	};

	getMembershipGroups = () => ({
		plans: this.state.membershipPlans || [],
		enrollments: this.getEnrollments(),
		customerActivity: this.getCustomerActivityMemberships(),
		billingAttention: this.getBillingAttentionMemberships(),
	});

	getAvailablePlans = () => this.state.membershipPlans || [];

	getSummary = () => {
		const plans = this.state.membershipPlans || [];
		const enrollments = this.getEnrollments();
		const customerActivity = this.props.customer ? this.getCustomerActivityMemberships() : enrollments;
		const activeMemberships = this.props.customer ? customerActivity.filter(isActiveMembership) : enrollments;
		const currentMemberships = activeMemberships.filter(isCurrentMembership);
		const billedMemberships = currentMemberships.filter((membership) => !isCoveredMembership(membership));
		const tokenizedMemberships = billedMemberships.filter(hasCardToken);
		const monthlyValue = this.props.customer
			? membershipMonthlyValue(billedMemberships)
			: resolvedAccountMembershipMonthlyValue(this.state.accountMonthlyValue, billedMemberships);
		const failedBills = customerActivity.reduce((total, membership) => total + failedBillingCount(membership), 0);
		const activeCount = this.effectiveActiveView() === "plans"
			? plans.reduce((total, plan) => total + (parseInt(plan.enrollment_count, 10) || 0), 0)
			: (this.props.customer ? currentMemberships.length : (typeof this.state.totalCount === "number" ? this.state.totalCount : enrollments.length));

		return {
			plans: plans.length,
			active: activeCount,
			tokenized: tokenizedMemberships.length,
			monthlyValue,
			failedBillingCount: failedBills,
		};
	};

	setActiveView = (activeView) => {
		if (this.props.customer || (activeView === "plans" && !this.canManageMembershipPlans())) {
			return;
		}

		this.setState({
			activeView,
			selectedPlanFilter: null,
			filter: this.buildEnrollmentFilter({activeView, selectedPlanFilter: null}),
			data: [],
			page: 0,
			pages: null,
			totalCount: null,
		}, () => {
			if (activeView === "plans") {
				this.fetchPlans();
			}
		});
	};

	setBillingStatusFilter = (billingStatusFilter) => {
		if (["all", "failed", "cancelled"].indexOf(billingStatusFilter) === -1 || this.state.billingStatusFilter === billingStatusFilter) {
			return;
		}

		const nextTableState = {
			...(this.tableState || {}),
			page: 0,
		};
		this.tableState = nextTableState;
		this.setState({
			billingStatusFilter,
			filter: this.buildEnrollmentFilter({billingStatusFilter}),
			data: [],
			page: 0,
			pages: null,
			totalCount: null,
		}, () => this.fetchEnrollments(nextTableState));
	};

	viewPlanCustomers = (plan) => {
		this.setState({
			activeView: "customers",
			selectedPlanFilter: plan,
			filter: this.buildEnrollmentFilter({activeView: "customers", selectedPlanFilter: plan}),
			data: [],
			page: 0,
			pages: null,
			totalCount: null,
		});
	};

	clearPlanCustomerFilter = () => {
		this.setState({
			selectedPlanFilter: null,
			filter: this.buildEnrollmentFilter({selectedPlanFilter: null}),
			page: 0,
			pages: null,
		});
	};

	handlePlanFilterSelectUpdate = (name, selectedPlanPkey, linked, selectedPlanFilter) => {
		this.setState({
			selectedPlanFilter,
			filter: this.buildEnrollmentFilter({selectedPlanFilter}),
			page: 0,
			pages: null,
		});
	};

	openEnrollmentModal = (plan = null) => {
		this.setState({
			selectedPlan: plan,
			enrollmentModal: true,
		});
	};

	closeEnrollmentModal = () => {
		this.setState({
			selectedPlan: null,
			enrollmentModal: false,
		});
	};

	handleEnrollmentComplete = () => {
		this.closeEnrollmentModal();
		this.fetchPlans();
		this.fetchEnrollments();
		if (typeof this.props.reloadPage === "function") {
			this.props.reloadPage();
		}
	};

	retryMembershipBilling = async (membership) => {
		const membershipPkey = parseInt(membership && membership.pkey, 10);
		if (!Number.isFinite(membershipPkey) || this.retryRequestInFlight) {
			return;
		}
		if (!hasFailedBilling(membership)) {
			API.Toast.warning("This membership does not have a failed bill to retry.", toastOptions);
			return;
		}
		if (normalizedMembershipStatus(membership) === "paused") {
			API.Toast.warning("Resume this membership before retrying billing.", toastOptions);
			return;
		}
		if (!hasCardToken(membership)) {
			API.Toast.error("Capture a card for this membership before retrying billing.", toastOptions);
			return;
		}

		const reactivatesMembership = isCanceledMembership(membership);
		const customerName = formatCustomerName(membership);
		const confirmation = reactivatesMembership
			? `Retry ${formatBillingAmount(membership)} for ${customerName}? A successful charge will reactivate this membership.`
			: `Retry ${formatBillingAmount(membership)} for ${customerName}?`;
		if (!window.confirm(confirmation)) {
			return;
		}

		this.retryRequestInFlight = true;
		this.setState({retryingMembership: membershipPkey});
		try {
			const requestType = "POST_BILL";
			const requestModel = new API.BuildClass(Types.Memberships._name, requestType);
			requestModel.object = {
				membership_action: membershipPkey,
				retry_canceled: reactivatesMembership,
			};
			const response = await requestModel.submit(null, requestType, true);
			if (response && response.success) {
				API.Toast.success(response.reactivated ? "Membership billed and reactivated successfully." : "Membership billed successfully.", toastOptions);
			} else {
				const message = typeof response === "string" ? response : (response && response.message);
				API.Toast.warning(message || "Membership billing retry failed.", toastOptions);
			}
			await this.fetchEnrollments(this.tableState);
		} catch (error) {
			API.Toast.error(error.message || "Unable to retry membership billing.", toastOptions);
		} finally {
			this.retryRequestInFlight = false;
			this.setState({retryingMembership: null});
		}
	};

	renderMembershipRetryButton = (membership) => {
		const membershipPkey = parseInt(membership.pkey, 10);
		const retrying = this.state.retryingMembership === membershipPkey;
		const paused = normalizedMembershipStatus(membership) === "paused";
		const retryableFailure = hasFailedBilling(membership);
		const retryDisabled = !!this.state.retryingMembership || !hasCardToken(membership) || paused || !retryableFailure;
		const retryTitle = !hasCardToken(membership)
			? "Capture a card before retrying billing"
			: (paused ? "Resume this membership before retrying billing" : (retryableFailure ? "Retry membership billing" : "No failed bill is available to retry"));

		return (
			<Button
				color="warning"
				size="sm"
				disabled={retryDisabled}
				onClick={() => this.retryMembershipBilling(membership)}
				title={retryTitle}
			>
				<i className={`fa ${retrying ? "fa-spinner fa-spin" : "fa-refresh"}`}/>&nbsp;&nbsp;{retrying ? "Retrying" : "Retry"}
			</Button>
		);
	};

	buildEnrollmentRequestModel = (tableState, countOnly = false) => {
		const page = tableState && Number.isFinite(tableState.page) ? tableState.page : (this.state.page || 0);
		const sorted = tableState && tableState.sorted && tableState.sorted.length
			? tableState.sorted
			: [{
				id: this.model.sort || "name",
				desc: this.model.order === "desc",
			}];
		const model = new API.BuildClass(Types.Memberships._name, API.RequestType.READ_ALL);

		model.limit = this.state.limit;
		model.offset = page * (this.state.limit || 0);
		model.sort = sorted[0] ? sorted[0].id : "name";
		model.order = sorted[0] && sorted[0].desc ? "desc" : "asc";
		model.search = this.state.search || "";
		if (typeof this.state.searchField !== "undefined" && this.state.searchField !== "") {
			model.search_field = this.state.searchField.value || this.state.searchField;
		}
		model.filter = {
			...this.buildEnrollmentFilter(),
			table: countOnly ? "count" : "firstLoad",
		};

		return {model, page, sorted};
	};

	fetchCustomerActivityMemberships = async (tableState) => {
		const requestType = "GET_CUSTOMER_MEMBERSHIPS";
		const requestModel = new API.BuildClass(Types.Memberships._name, requestType);
		requestModel.object = {
			customer_lookup: this.props.customer,
			include_activity: true,
		};
		const response = await requestModel.submit(null, requestType, true);
		let memberships = Array.isArray(response && response.memberships) ? response.memberships : [];
		if (this.state.selectedPlanFilter) {
			memberships = memberships.filter((membership) => membershipMatchesPlan(membership, this.state.selectedPlanFilter));
		}

		const search = String(this.state.search || "").trim().toLowerCase();
		if (search) {
			memberships = memberships.filter((membership) => [
				membership.name,
				payingCustomerName(membership),
				membershipActivityStatus(membership),
				membershipRole(membership),
			].some((searchValue) => String(searchValue || "").toLowerCase().indexOf(search) !== -1));
		}

		const page = tableState && Number.isFinite(tableState.page) ? tableState.page : 0;
		const sorted = tableState && tableState.sorted && tableState.sorted.length
			? tableState.sorted
			: [{id: "name", desc: false}];
		const sort = sorted[0];
		memberships.sort((left, right) => {
			const leftValue = sort.id === "status" ? membershipActivityStatus(left) : (left[sort.id] || "");
			const rightValue = sort.id === "status" ? membershipActivityStatus(right) : (right[sort.id] || "");
			const comparison = String(leftValue).localeCompare(String(rightValue), undefined, {numeric: true});
			return sort.desc ? -comparison : comparison;
		});

		const total = memberships.length;
		const offset = page * this.state.limit;
		return {
			items: memberships.slice(offset, offset + this.state.limit),
			page,
			pages: Math.max(1, Math.ceil(total / this.state.limit)),
			sorted,
			total,
		};
	};

	fetchEnrollments = async (tableState) => {
		const requestGeneration = (this.enrollmentFetchGeneration || 0) + 1;
		this.enrollmentFetchGeneration = requestGeneration;
		if (this.effectiveActiveView() === "plans" && !this.props.customer) {
			return;
		}
		if (this.enrollmentFetchInFlight) {
			this.enrollmentFetchQueued = true;
			this.queuedEnrollmentTableState = tableState || null;
			return;
		}

		const requestedCustomer = this.props.customer;
		const nextTableState = tableState || this.tableState || {
			page: this.state.page || 0,
			pageSize: this.state.limit,
			sorted: [{
				id: this.model.sort || "name",
				desc: this.model.order === "desc",
			}],
			filtered: [],
		};

		this.tableState = nextTableState;
		this.enrollmentFetchInFlight = true;
		this.setState({loading: true});

		try {
			if (this.props.customer) {
				const customerActivity = await this.fetchCustomerActivityMemberships(nextTableState);
				if (requestGeneration !== this.enrollmentFetchGeneration
					|| requestedCustomer !== this.props.customer
				) {
					return;
				}
				this.model.sort = customerActivity.sorted[0] ? customerActivity.sorted[0].id : this.model.sort;
				this.model.order = customerActivity.sorted[0] && customerActivity.sorted[0].desc ? "desc" : "asc";
				this.setState({
					data: customerActivity.items,
					loading: false,
					page: customerActivity.page,
					pages: customerActivity.pages,
					totalCount: customerActivity.total,
				});
				return;
			}
			const {model, page, sorted} = this.buildEnrollmentRequestModel(nextTableState, false);
			const data = await model.submit(null, API.RequestType.READ_ALL);
			if (requestGeneration !== this.enrollmentFetchGeneration
				|| requestedCustomer !== this.props.customer
			) {
				return;
			}
			let total = parseInt(data && data.total, 10);
			let pages = Number.isFinite(total) && this.state.limit
				? Math.max(1, Math.ceil(total / this.state.limit))
				: 1;

			if (!Number.isFinite(total) || total === this.state.limit) {
				const countRequest = this.buildEnrollmentRequestModel({...nextTableState, page, sorted}, true);
				const countData = await countRequest.model.submit(null, API.RequestType.READ_ALL);
				if (requestGeneration !== this.enrollmentFetchGeneration
					|| requestedCustomer !== this.props.customer
				) {
					return;
				}
				total = parseInt(countData && countData.total, 10);
				pages = Number.isFinite(total) && this.state.limit
					? Math.max(1, Math.ceil(total / this.state.limit))
					: 1;
			}

			this.model.sort = sorted[0] ? sorted[0].id : this.model.sort;
			this.model.order = sorted[0] && sorted[0].desc ? "desc" : "asc";
			this.setState({
				data: (data && data.items) || [],
				loading: false,
				page,
				pages: Number.isFinite(pages) ? pages : 1,
				totalCount: Number.isFinite(total) ? total : ((data && data.items) || []).length,
			});
		} catch (error) {
			if (requestGeneration === this.enrollmentFetchGeneration
				&& requestedCustomer === this.props.customer
			) {
				console.error(error);
				API.Toast.error(error.message || "Unable to load customer memberships.", toastOptions);
				this.setState({loading: false});
			}
		} finally {
			this.enrollmentFetchInFlight = false;
			if (this.enrollmentFetchQueued) {
				const queuedTableState = this.queuedEnrollmentTableState;
				this.enrollmentFetchQueued = false;
				this.queuedEnrollmentTableState = null;
				this.fetchEnrollments(queuedTableState);
			}
		}
	};

	reloadMembershipSearch = () => {
		if (this.effectiveActiveView() === "plans" && !this.props.customer) {
			this.fetchPlans();
			return;
		}

		this.fetchEnrollments(this.tableState);
	};

	reloadTable = () => {
		// Immediate reload for the search button / filters; cancel any pending idle search first.
		if (this.membershipSearchTimeout) {
			clearTimeout(this.membershipSearchTimeout);
			this.membershipSearchTimeout = null;
		}
		this.reloadMembershipSearch();
	};

	fetchData = async (state) => {
		if (this.effectiveActiveView() !== "plans" || this.props.customer) {
			return this.fetchEnrollments(state);
		}

		return this.fetchPlans();
	};

	fetchMembershipData = (tableState) => {
		if (this.effectiveActiveView() === "plans" && !this.props.customer) {
			this.fetchPlans();
			return;
		}

		this.fetchEnrollments(tableState);
	};

	refreshMembershipData = () => {
		this.fetchPlans();
		if (this.effectiveActiveView() === "plans" && !this.props.customer) {
			return;
		}

		this.fetchEnrollments(this.tableState || {
			page: this.state.page || 0,
			pageSize: this.state.limit,
			sorted: [],
			filtered: [],
		});
	};

	handleToolbarAction = () => {
		const availablePlans = this.getAvailablePlans();
		if (this.effectiveActiveView() === "billing") {
			return;
		}
		if (this.effectiveActiveView() === "plans") {
			if (!this.canManageMembershipPlans()) {
				return;
			}
			if (typeof this.props.onAddPlan === "function") {
				this.props.onAddPlan();
			}
			return;
		}

		if (!availablePlans.length) {
			API.Toast.error("Create a membership plan before adding customer memberships.", toastOptions);
			return;
		}
		if (this.props.customer && !this.state.allowMultipleMemberships && this.getMembershipGroups().enrollments.length > 0) {
			API.Toast.error("This customer already has a membership. Turn on multiple memberships before adding another.", toastOptions);
			return;
		}

		this.openEnrollmentModal(this.state.selectedPlanFilter);
	};

	renderToolbarAction = () => {
		if (this.effectiveActiveView() === "billing") {
			return null;
		}
		if (this.effectiveActiveView() === "plans" && !this.canManageMembershipPlans()) {
			return null;
		}

		const customerView = this.effectiveActiveView() === "customers";
		const label = this.props.customer ? "Add Membership" : (customerView ? "Add Customer Membership" : "Add Plan");
		const loadingPlans = this.state.membershipPlansLoading;
		const fixedCustomerBlocked = this.props.customer && !this.state.allowMultipleMemberships && this.getMembershipGroups().enrollments.length > 0;

		return (
			<Button
				type="button"
				className="membership-toolbar-action primary-button"
				disabled={loadingPlans || fixedCustomerBlocked}
				onClick={this.handleToolbarAction}
			>
				<i className={`fa ${customerView ? "fa-user-plus" : "fa-id-card-o"}`}/>
				&nbsp;&nbsp;{fixedCustomerBlocked ? "Membership Exists" : (loadingPlans ? "Loading Plans" : label)}
			</Button>
		);
	};

	renderCustomerMembershipActivityAction = () => {
		if (!this.props.customer || !this.canManageMembershipPlans()) {
			return null;
		}

		return (
			<Link to={customerMembershipActivityUrl(this.props.customer)}>
				<Button type="button" className="secondary-button">
					<i className="fa fa-history"/>&nbsp;&nbsp;All Activity
				</Button>
			</Link>
		);
	};

	renderTableRefresh = () => {
		const activeView = this.effectiveActiveView();
		const label = activeView === "plans" ? "Refresh Plans" : (activeView === "billing" ? "Refresh Failed and Cancelled Memberships" : "Refresh Customer Memberships");

		return (
			<Button
				type="button"
				size="sm"
				outline
				className="membership-table-refresh outline-button-secondary"
				disabled={this.state.loading}
				onClick={this.refreshMembershipData}
				title={label}
				aria-label={label}
			>
				<i className={`fa fa-refresh${this.state.loading ? " fa-spin" : ""}`}/>
			</Button>
		);
	};

	renderMembershipSettingsToggle = () => {
		if (this.props.customer || !this.canManageMembershipPlans() || this.effectiveActiveView() === "billing") {
			return null;
		}

		const {allowMultipleMemberships, membershipSettingsLoading, membershipSettingsSaving} = this.state;
		const disabled = membershipSettingsLoading || membershipSettingsSaving;

		return (
			<div className="membership-settings-toggle">
				<span>Customer Limit</span>
				<ButtonGroup>
					<Button
						type="button"
						size="sm"
						className="primary-button-buttongroup"
						color='primary'
						outline={!!allowMultipleMemberships === false}
						active={!!allowMultipleMemberships === true}
						disabled={disabled}
						onClick={() => this.updateAllowMultipleMemberships(true)}
					>
						Multiple Allowed
					</Button>
					<Button
						type="button"
						size="sm"
						className="primary-button-buttongroup"
						color='primary'
						outline={!!allowMultipleMemberships === true}
						active={!!allowMultipleMemberships === false}
						disabled={disabled}
						onClick={() => this.updateAllowMultipleMemberships(false)}
					>
						One Per Customer
					</Button>
				</ButtonGroup>
			</div>
		);
	};

	renderPlanSelectFilter = (plans) => {
		if (["customers", "billing"].indexOf(this.effectiveActiveView()) === -1) {
			return null;
		}

		const selectedPlanPkey = this.state.selectedPlanFilter ? this.state.selectedPlanFilter.pkey : "";

		return (
			<div className="membership-plan-select-filter">
				<Framework.Elements.Select
					shouldLoad={true}
					value={selectedPlanPkey}
					error=""
					objects={plans}
					property={this.model.properties.membership_plan || {name: "membership_plan"}}
					label="All Membership Plans"
					update={this.handlePlanFilterSelectUpdate}
					nullable={true}
					default={{value: "", label: "All Membership Plans"}}
					disabled={!plans.length}
					limit={0}
				/>
			</div>
		);
	};

	renderBillingStatusFilter = () => {
		if (this.effectiveActiveView() !== "billing") {
			return null;
		}

		return (
			<ButtonGroup className="membership-billing-status-filter">
				{[
					{value: "all", label: "All"},
					{value: "failed", label: "Failed"},
					{value: "cancelled", label: "Cancelled"},
				].map((filter) => {
					const selected = this.state.billingStatusFilter === filter.value;
					return (
						<Button
							key={`membership-billing-status-${filter.value}`}
							type="button"
							size="sm"
							color={selected ? "warning" : "secondary"}
							outline={!selected}
							aria-pressed={selected}
							onClick={() => this.setBillingStatusFilter(filter.value)}
						>
							{filter.label}
						</Button>
					);
				})}
			</ButtonGroup>
		);
	};

	renderPlanRows = (plans) => {
		const columns = [
			{
				Header: "Membership Plan",
				id: "name",
				accessor: membership => membership.name || "Untitled Membership",
			},
			{
				Header: "Billing",
				id: "cost_per_month",
				accessor: formatBillingAmount,
			},
			{
				Header: "Discount",
				id: "inventory_group_discounts",
				accessor: formatInventoryGroupDiscounts,
			},
			{
				Header: "Range Time",
				id: "free_range_minutes",
				className: 'center-column',
				accessor: formatRangeTime,
			},
			{
				Header: "People",
				id: "customers",
				accessor: membership => parseInt(membership.people_count, 10) || parseInt(membership.enrollment_count, 10) || 0,
				className: 'center-column',
				Cell: props => (
					<Button
						type="button"
						color="link"
						className="membership-customer-count"
						onClick={() => this.viewPlanCustomers(props.original)}
					>
						<i className="fa fa-users"/>&nbsp;&nbsp;{props.value} View
					</Button>
				),
			},
			{
				Header: "",
				id: "actions",
				className: "membership-actions-cell",
				filterable: false,
				sortable: false,
				width: 180,
				Cell: props => (
					<div className="membership-row-actions">
						<Button className="primary-button" size="sm" onClick={() => this.openEnrollmentModal(props.original)}>
							<i className="fa fa-user-plus"/>&nbsp;&nbsp;Attach
						</Button>
						<Link to={`/memberships/${props.original.pkey}`}>
							<Button className="secondary-button" size="sm">
								<Edit fontSize="small"/>
							</Button>
						</Link>
					</div>
				),
			},
		];

		return this.renderPlansTable(plans, columns, "No membership plans found");
	};

	renderEnrollmentRows = (enrollments) => {
		const columns = this.props.customer ? [] : [
			{
				Header: "Customer",
				id: "customer",
				accessor: formatCustomerName,
				Cell: props => {
					const customerPkey = props.original.customer || (props.original.customers && props.original.customers.pkey);
					return customerPkey ?
						<Link to={`/customers/${customerPkey}`}>{props.value}</Link>
						: props.value;
				},
			},
		];
		if (this.props.customer) {
			columns.push(
				{
					Header: "Role",
					id: "membership_role",
					accessor: membership => {
						if (isRemovedCoveredMembership(membership)) {
							return "Former covered member";
						}
						return isCoveredMembership(membership) ? "Covered member" : "Paying member";
					},
				},
				{
					Header: "Billing Contact",
					id: "paying_customer",
					accessor: payingCustomerName,
					sortable: false,
					Cell: props => {
						const payingCustomer = props.original.paying_customer;
						return payingCustomer && payingCustomer.pkey
							? <Link to={`/customers/${payingCustomer.pkey}`}>{props.value}</Link>
							: props.value;
					},
				},
			);
		}

		columns.push({
			Header: "Covered",
			id: "covered_member_count",
			sortable: false,
			accessor: membership => parseInt(membership.covered_member_count, 10) || 0,
			Cell: props => props.value > 0 ? (
				<Link
					to={`/memberships/${props.original.pkey}#membership-members`}
					title="View covered members"
				>
					{props.value} member{props.value === 1 ? "" : "s"}
				</Link>
			) : 0,
		});

		columns.push(
			{
				Header: "Membership",
				id: "name",
				accessor: membership => membership.name || "Untitled Membership",
			},
			{
				Header: "Billing",
				id: "cost_per_month",
				accessor: membership => this.props.customer ? customerMembershipBillingAmount(membership) : formatBillingAmount(membership),
			},
		);

		if (this.props.customer) {
			columns.push(
				{
					Header: "Status",
					id: "status",
					accessor: membershipActivityStatus,
					Cell: props => (
						<span className={`membership-history-status ${membershipActivityStatusClass(props.original)}`}>
							{props.value}
						</span>
					),
				},
				{
					Header: "Cancelled",
					id: "cancelled_at",
					accessor: membership => membership.cancelled_at ? formatDate(membership.cancelled_at) : "-",
				},
			);
		}

		columns.push(
			{
				Header: "Discount",
				id: "inventory_group_discounts",
				accessor: formatInventoryGroupDiscounts,
			},
			{
				Header: "Range Time",
				id: "free_range_minutes",
				className: 'center-column',
				accessor: formatRangeTime,
			},
			{
				Header: "Card",
				id: "card",
				sortable: false,
				accessor: membership => isFreeMembership(membership)
					? "Not required"
					: (this.props.customer ? customerMembershipCardLabel(membership) : formatCardLabel(membership)),
			},
			{
				Header: "Terminal",
				id: "terminal",
				className: 'center-column',
				sortable: false,
				accessor: membership => isFreeMembership(membership)
					? "Not required"
					: (this.props.customer && (isCoveredMembership(membership) || isRemovedCoveredMembership(membership))
						? "Payer terminal"
						: formatTerminalName(membership)),
			},
			{
				Header: "Start",
				id: "start_date",
				className: 'center-column',
				accessor: membership => formatDate(membership.start_date),
			},
			...(enrollments.some(membership => !!membershipEndDate(membership)) ? [{
				Header: "End Date",
				id: "end_date",
				accessor: membership => membershipEndDate(membership)
					? formatDate(membership.end_date)
					: "-",
			}] : []),
			{
				Header: "Next Bill",
				id: "next_bill_date",
				className: 'center-column',
				accessor: membership => {
					if (isFreeMembership(membership)) {
						return "Not required";
					}
					return this.props.customer && (isCoveredMembership(membership) || isRemovedCoveredMembership(membership))
						? `Billed to ${payingCustomerName(membership)}`
						: formatDate(membership.next_bill_date);
				},
				Cell: props => (
					!isFreeMembership(props.original)
					&& !(this.props.customer && (isCoveredMembership(props.original) || isRemovedCoveredMembership(props.original)))
					&& hasNextBillAfterMembershipEnd(props.original)
						? <span className="membership-next-bill-after-end" title="This billing date falls after the membership end date.">{props.value}</span>
						: props.value
				),
			},
			{
				Header: "Last Result",
				id: "last_billing_message",
				className: 'center-column',
				accessor: membership => membership.last_billing_message || membership.last_billing_status || "No attempts",
			},
		);

		columns.push(
			{
				Header: "Failed",
				id: "failed_billing_count",
				className: 'center-column',
				accessor: failedBillingCount,
				Cell: props => (
					props.value > 0 && this.canManageMembershipPlans() ?
						<Link className="membership-failed-billing-link" to={failedBillingDetailsUrl(props.original)}>
							{props.value}
						</Link>
						: props.value
				),
			},
		);

		if (this.props.customer && this.canManageMembershipPlans()) {
			columns.push(
				{
					Header: "Retry",
					id: "retry",
					accessor: membership => membership.pkey,
					filterable: false,
					sortable: false,
					width: 90,
					Cell: props => membershipRole(props.original) === "payer" && !isDeletedMembership(props.original) && !isFreeMembership(props.original) && !hasMembershipReachedBillingEnd(props.original) && hasFailedBilling(props.original)
						? this.renderMembershipRetryButton(props.original)
						: "-",
				},
				{
					Header: "Activity",
					id: "activity",
					accessor: membership => membership.pkey,
					filterable: false,
					sortable: false,
					width: 95,
					Cell: props => (
						<Link to={membershipActivityUrl(props.original)}>
							<Button className="secondary-button" size="sm">
								<i className="fa fa-history"/>&nbsp;&nbsp;View
							</Button>
						</Link>
					),
				},
			);
		}

		columns.push(
			{
				Header: "",
				id: "edit",
				className: "button-cell d-flex align-items-center ",
				filterable: false,
				sortable: false,
				width: 45,
				accessor: membership => membership.pkey,
				Cell: props => isDeletedMembership(props.original) || isRemovedCoveredMembership(props.original) ? "-" : (
					<Link to={`/memberships/${props.original.pkey}`}>
						<Button color="green-meadow" size="sm" block={true} style={{ marginTop: '2px' }}>
							<Edit fontSize="small"/>
						</Button>
					</Link>
				),
			},
		);

		return this.renderEnrollmentsTable(enrollments, columns, this.props.customer ? "No membership activity found" : "No customer memberships found");
	};

	renderBillingAttentionRows = (memberships) => {
		const columns = [
			{
				Header: "Customer",
				id: "customer",
				accessor: formatCustomerName,
				Cell: props => {
					const customerPkey = props.original.customer || (props.original.customers && props.original.customers.pkey);
					return customerPkey ?
						<Link to={`/customers/${customerPkey}`}>{props.value}</Link>
						: props.value;
				},
			},
			{
				Header: "Phone",
				id: "phone",
				accessor: customerPhone,
				sortable: false,
				Cell: props => (
					props.value ?
						<a className="membership-phone-link" href={customerPhoneHref(props.original)}>
							<i className="fa fa-phone"/>&nbsp;&nbsp;{props.value}
						</a>
						: "No phone"
				),
			},
			{
				Header: "Membership",
				id: "name",
				accessor: membership => membership.name || "Untitled Membership",
			},
			{
				Header: "Billing",
				id: "cost_per_month",
				accessor: formatBillingAmount,
			},
			{
				Header: "Status",
				id: "status",
				accessor: billingAttentionStatus,
				Cell: props => (
					<span className="membership-history-status failed">{props.value}</span>
				),
			},
			{
				Header: "Failed",
				id: "failed_billing_count",
				accessor: failedBillingCount,
				Cell: props => (
					<Link className="membership-failed-billing-link" to={failedBillingDetailsUrl(props.original)}>
						{props.value}
					</Link>
				),
			},
			{
				Header: "Cancelled",
				id: "cancelled_at",
				accessor: membership => membership.cancelled_at ? formatDate(membership.cancelled_at) : "-",
			},
			{
				Header: "Last Result",
				id: "last_billing_message",
				accessor: membership => membership.last_billing_message || "No billing response",
				Cell: props => (
					<Link title={props.value} to={failedBillingDetailsUrl(props.original)}>{props.value}</Link>
				),
			},
			{
				Header: "",
				id: "actions",
				className: "membership-actions-cell",
				filterable: false,
				sortable: false,
				width: 285,
				Cell: props => {
					const membership = props.original;
					const phoneHref = customerPhoneHref(membership);

					return (
						<div className="membership-row-actions">
							{phoneHref ?
								<a className="btn btn-info btn-sm" href={phoneHref}>
									<i className="fa fa-phone"/>&nbsp;&nbsp;Call
								</a>
								: null}
							{this.renderMembershipRetryButton(membership)}
							<Link to={canManageMembershipPlansForRole(this.props.roles)
								? membershipActivityUrl(membership)
								: `/memberships/${membership.pkey}`}>
								<Button className="secondary-button" size="sm">Details</Button>
							</Link>
						</div>
					);
				},
			},
		];

		const emptyText = this.state.billingStatusFilter === "failed"
			? "No failed memberships found"
			: (this.state.billingStatusFilter === "cancelled" ? "No cancelled memberships found" : "No failed or cancelled memberships found");

		return this.renderEnrollmentsTable(memberships, columns, emptyText);
	};

	renderPlanCustomerFilter = () => {
		const {selectedPlanFilter} = this.state;
		if (!selectedPlanFilter) {
			return null;
		}

		return (
			<div className="membership-plan-filter">
				<div>
					<span>Attached Customers</span>
					<strong>{selectedPlanFilter.name || "Untitled Membership"}</strong>
				</div>
				<Button type="button" className="secondary-button" size="sm" onClick={this.clearPlanCustomerFilter}>
					<i className="fa fa-times"/>&nbsp;&nbsp;Show All Customers
				</Button>
			</div>
		);
	};

	renderPlansTable = (data, columns, emptyText) => (
		<ReactTable
			key={`${this.state.key}-${this.state.activeView}-plans`}
			columns={columns}
			manual
			data={data}
			pages={1}
			loading={this.state.membershipPlansLoading}
			loadingText="Requesting Data"
			noDataText={emptyText}
			expanderDefaults={{
				sortable: true,
				resizable: true,
				filterable: false,
				width: undefined,
			}}
			defaultSorted={[
				{
					id: "name",
					desc: false,
				},
			]}
			showPagination={false}
			defaultPageSize={data.length || 1}
			pageSize={data.length || 1}
			minRows={0}
			className="-striped -highlight memberships-table"
		/>
	);

	renderEnrollmentsTable = (data, columns, emptyText) => {
		const {pages, loading, limit, page} = this.state;
		const totalPages = typeof pages === "number" && pages > 0 ? pages : 1;

		return (
			<ReactTable
				key={`${this.state.key}-${this.state.activeView}-${this.state.selectedPlanFilter ? this.state.selectedPlanFilter.pkey : "all"}`}
				columns={columns}
				manual
				data={data}
				pages={totalPages}
				loading={loading}
				loadingText="Requesting Data"
				noDataText={emptyText}
				onFetchData={this.fetchMembershipData}
				onPageSizeChange={this.onPageSizeChange}
				onSortedChange={this.onSortedChange}
				page={page}
				onPageChange={this.onPageChange}
				expanderDefaults={{
					sortable: true,
					resizable: true,
					filterable: false,
					width: undefined,
				}}
				defaultSorted={[
					{
						id: this.model.sort,
						desc: this.model.order === "desc",
					},
				]}
				showPagination={true}
				showPaginationTop={false}
				showPaginationBottom={true}
				defaultPageSize={limit}
				pageSize={limit}
				minRows={0}
				className="-striped -highlight memberships-table"
			/>
		);
	};

	render() {
		const {loading} = this.state;
		const customerView = !!this.props.customer;
		const canManagePlans = this.canManageMembershipPlans();
		const activeView = this.effectiveActiveView();
		const {plans, enrollments, customerActivity, billingAttention} = this.getMembershipGroups();
		const availablePlans = this.getAvailablePlans();
		const summary = this.getSummary();
		const customerMembershipRows = customerView ? customerActivity : enrollments;
		const currentRows = activeView === "plans" ? plans : (activeView === "billing" ? billingAttention : customerMembershipRows);
		const currentRowCount = activeView === "plans" || typeof this.state.totalCount !== "number"
			? currentRows.length
			: this.state.totalCount;
		const billingRowsLabel = this.state.billingStatusFilter === "failed"
			? "failed memberships"
			: (this.state.billingStatusFilter === "cancelled" ? "cancelled memberships" : "failed / cancelled memberships");
		const currentRowsLabel = activeView === "plans"
			? "plans"
			: (activeView === "billing" ? billingRowsLabel : (customerView ? "membership activity records" : "customer memberships"));
		return (
			<div className="memberships-collection">
				{activeView !== "billing" && (customerView || canManagePlans) &&
				<div className="membership-summary-grid">
					<div className="membership-summary-tile">
						<span className="membership-summary-label">{customerView || !canManagePlans ? "Memberships" : "Plans"}</span>
						<strong>{customerView ? currentRowCount : (!canManagePlans ? enrollments.length : summary.plans)}</strong>
					</div>
					<div className="membership-summary-tile">
						<span className="membership-summary-label">{customerView ? "Current" : "Customers"}</span>
						<strong>{summary.active}</strong>
					</div>
					<div className="membership-summary-tile">
						<span className="membership-summary-label">Monthly Value</span>
						<strong>{formatCurrency(summary.monthlyValue)}</strong>
					</div>
					<div className="membership-summary-tile warning">
						<span className="membership-summary-label">Failed Bills</span>
						<strong>
							{summary.failedBillingCount > 0 && canManagePlans ?
								<Link className="membership-summary-details-link" to={failedBillingDetailsUrl(null, this.props.customer)}>
									{summary.failedBillingCount}
								</Link>
								: summary.failedBillingCount}
						</strong>
					</div>
				</div>}
				{customerView ? customerActivity.map((membership) => (
					<MembershipBillingAlert
						key={`customer-membership-billing-alert-${membership.pkey}`}
						membership={membership}
						onCancelled={this.refreshMembershipData}
					/>
				)) : null}
				<Card className="memberships-table-card">
					<CardContent>
						<div className="memberships-view-toolbar">
							{customerView || !canManagePlans ? null :
								<ButtonGroup className="membership-view-tabs">
									<Button
										type="button"
										className="primary-button-buttongroup"
										color='primary'
										active={activeView === "plans"}
										outline={activeView !== "plans"}
										onClick={() => this.setActiveView("plans")}
									>
										<i className="fa fa-list"/>&nbsp;&nbsp;Plans
									</Button>
									<Button
										type="button"
										className="primary-button-buttongroup"
										color='primary'
										active={activeView === "customers"}
										outline={activeView !== "customers"}
										onClick={() => this.setActiveView("customers")}
									>
										<i className="fa fa-users"/>&nbsp;&nbsp;Customer Memberships
									</Button>
									<Button
										type="button"
										color={activeView === "billing" ? "warning" : "secondary"}
										outline={activeView !== "billing"}
										onClick={() => this.setActiveView("billing")}
									>
										<i className="fa fa-exclamation-triangle"/>&nbsp;&nbsp;Failed / Cancelled
									</Button>
							</ButtonGroup>}
							<div className="memberships-result-count">
								{currentRowCount} {currentRowsLabel}
								{customerView ? ` for ${this.props.customerName || `customer #${this.props.customer}`}` : ""}
								{this.state.selectedPlanFilter ? ` for ${this.state.selectedPlanFilter.name || "selected plan"}` : ""}
							</div>
							<div className="memberships-toolbar-action-wrap">
								{this.renderMembershipSettingsToggle()}
								{this.renderCustomerMembershipActivityAction()}
								{this.renderToolbarAction()}
							</div>
						</div>
						<div className="table-bar memberships-table-bar">
							<div className="d-flex memberships-search-filters">
								{this.renderSearch(activeView === "plans" ? "Plans" : (activeView === "billing" ? "Failed / Cancelled" : "Memberships"))}
								{this.renderBillingStatusFilter()}
								{this.renderPlanSelectFilter(availablePlans)}
								{this.renderTableRefresh()}
							</div>
						</div>
						{activeView === "customers" && !customerView ? this.renderPlanCustomerFilter() : null}
						{activeView === "plans"
							? this.renderPlanRows(plans)
							: (activeView === "billing" ? this.renderBillingAttentionRows(billingAttention) : this.renderEnrollmentRows(customerMembershipRows))}
						<Framework.Elements.LoadingSpinner isLoading={loading}/>
					</CardContent>
				</Card>
				{this.state.enrollmentModal ? (
					<Objects.MembershipEnrollmentModal
						isOpen={this.state.enrollmentModal}
						plan={this.state.selectedPlan}
						plans={availablePlans}
						customer={this.props.customer}
						customerName={this.props.customerName}
						customerMemberships={enrollments}
						allowMultipleMemberships={this.state.allowMultipleMemberships}
						coveredMembersReady={this.state.coveredMembersReady}
						coveredMemberLimit={this.state.coveredMemberLimit}
						onClose={this.closeEnrollmentModal}
						onComplete={this.handleEnrollmentComplete}
					/>
				) : null}
			</div>
		);
	}
}
